import { Skeleton, SkeletonCircle } from '@doccareplus/ui';

/**
 * Loading placeholders for the profiles feature. Each is a `role="status"`
 * region (with an sr-only label) wrapping `aria-hidden` shimmer primitives,
 * so assistive tech announces "loading" once instead of reading every block.
 * Shapes mirror the real profile-row cards (avatar + name/meta + action).
 */

function LoadingRegion({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="status" aria-label={label}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** A single profile row: avatar, name/meta lines, and an action-button-sized bar. */
function ProfileRowSkeleton() {
  return (
    <div className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-1)]" aria-hidden>
      <div className="flex items-center gap-3">
        <SkeletonCircle size={40} />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-2/5" />
          <Skeleton className="h-3 w-3/5" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
      <Skeleton className="mt-3 h-8 w-full rounded-[10px]" />
    </div>
  );
}

/** A stacked list of profile-row placeholders, used for both linked and found profile sections. */
export function ProfilesListSkeleton({ label, rows = 3 }: { label: string; rows?: number }) {
  return (
    <LoadingRegion label={label}>
      <div className="flex flex-col gap-3">
        {Array.from({ length: rows }).map((_, i) => (
          <ProfileRowSkeleton key={i} />
        ))}
      </div>
    </LoadingRegion>
  );
}
